export const CESSION_OVERTURNING_BUTTON_LABELS = {
  CREATE_CESSION: 'Criar Cessão',
  CREATE_OVERTURNING: 'Criar Tombamento',
} as const;

export const CESSION_OVERTURNING_LABELS = {
  ACQUISITION_VALUE: 'Valor de Aquisição',
  CATEGORY: 'Categoria',
  CREATED_AT: 'Criado em',
  CUT_OFF_DATE: 'Data de Corte',
  DATE_CESSION: 'Data de Cessão',
  FINANCED_VALUE: 'Valor Financiado',
  GROSS_AMOUNT: 'Valor Bruto',
  ID: 'ID',
  ID_CESSION: 'ID da Cessão',
  INTERMEDIATOR: 'Intermediador',
  SECURITIZATION_ORIGIN: 'Origem da Securitização',
  SECURITIZATION_TARGET: 'Destino da Securitização',
  SECURITIZER: 'Securitizadora',
  STATUS: 'Status',
  TYPE: 'Tipo',
  UPDATED_AT: 'Última atualização',
} as const;

export const CESSION_OVERTURNING_TABLE_COLUMNS = [
  { name: CESSION_OVERTURNING_LABELS.DATE_CESSION, sort: true, value: 'operation_date' },
  { name: CESSION_OVERTURNING_LABELS.ID, sort: true, value: 'cession_id' },
  { name: CESSION_OVERTURNING_LABELS.CATEGORY, sort: true, value: 'operation_type' },
  { name: CESSION_OVERTURNING_LABELS.SECURITIZER, sort: true, value: 'securitization_name' },
  { name: CESSION_OVERTURNING_LABELS.FINANCED_VALUE, sort: true, value: 'financed_amount' },
  { name: CESSION_OVERTURNING_LABELS.GROSS_AMOUNT, sort: true, value: 'gross_amount' },
  { name: CESSION_OVERTURNING_LABELS.ACQUISITION_VALUE, sort: true, value: 'acquisition_amount' },
  { name: CESSION_OVERTURNING_LABELS.STATUS, sort: true, value: 'status' },
] as const;

export const CESSION_TYPE_OPTIONS = [
  { name: 'Cessão', value: 'new_cession' },
  { name: 'Tombamento', value: 'overturning' },
] as const;

export const CESSION_STATUS_OPTIONS = [
  { name: 'Aguardando Geração de Endosso em Preto', value: 'awaiting_black_endorsement_generation' },
  { name: 'Aguardando Retorno de Cessão do Intermediador', value: 'awaiting_return_cession_intermediator' },
  { name: 'Cancelado pelo Usuário', value: 'cancelled_by_user' },
  { name: 'Cessão Cancelada pelo Intermediador', value: 'cession_cancelled_by_intermediator' },
  { name: 'Securitização Alterada', value: 'changed_securitization' },
  { name: 'Concluído', value: 'done' },
  { name: 'Endosso em Preto Enviado para Assinatura', value: 'endorsement_black_sent_for_signature' },
  { name: 'Gerando Endosso em Preto', value: 'generating_endorsement_black' },
  { name: 'Em Criação', value: 'in_creation' },
  { name: 'Em Andamento', value: 'in_progress' },
  { name: 'Invalidado pelo Intermediador', value: 'invalidated_by_intermediator' },
  { name: 'Falha na Importação de Financiamento', value: 'failed_import_financing' },
  { name: 'Falha na Importação de Parcelas', value: 'failed_import_installments' },
  { name: 'Falha no Intermediador', value: 'failed_intermediator' },
  { name: 'Parcialmente Concluído', value: 'partially_done' },
  { name: 'Pendente de Envio ao Intermediador', value: 'pending_send_intermediator' },
  { name: 'Enviando Garantia', value: 'sending_backing' },
  { name: 'Enviando Cessão ao Intermediador', value: 'sending_cession_intermediator' },
  { name: 'Sincronizando Parcelas', value: 'sync_installments' },
  { name: 'Parcelas Sincronizadas', value: 'synced_installments' },
] as const;

export const CESSION_SEARCH_TYPE_OPTIONS = [
  { name: 'ID Financiamento', value: 'financing_id' },
  { name: 'ID Cessão', value: 'cession_id' },
  { name: 'CCB', value: 'ccb' },
] as const;

export const CESSION_CATEGORY_COLOR_CLASS = {
  new_cession: 'green',
  overturning: 'purple',
  renegotiation: 'blue',
} as const;

export const CESSION_CATEGORY_LABELS = {
  new_cession: 'Cessão',
  overturning: 'Tombamento',
  renegotiation: 'Renegociação',
} as const;

export type CessionStatus =
  | 'awaiting_black_endorsement_generation'
  | 'awaiting_return_cession_intermediator'
  | 'cancelled_by_user'
  | 'cession_cancelled_by_intermediator'
  | 'changed_securitization'
  | 'done'
  | 'endorsement_black_sent_for_signature'
  | 'failed_import_financing'
  | 'failed_import_installments'
  | 'failed_intermediator'
  | 'generating_endorsement_black'
  | 'in_creation'
  | 'in_progress'
  | 'invalidated_by_intermediator'
  | 'partially_done'
  | 'pending_send_intermediator'
  | 'sending_backing'
  | 'sending_cession_intermediator'
  | 'sync_installments'
  | 'synced_installments';

/**
 * Get CSS color class for cession status
 * @param status - Cession status
 * @returns CSS color class name
 */
export function getCessionStatusColorClass(status?: CessionStatus): string {
  if (!status) return 'gray';

  const colorMap: Record<CessionStatus, string> = {
    awaiting_black_endorsement_generation: 'yellow',
    awaiting_return_cession_intermediator: 'yellow',
    cancelled_by_user: 'red',
    cession_cancelled_by_intermediator: 'red',
    changed_securitization: 'blue',
    done: 'green',
    endorsement_black_sent_for_signature: 'blue',
    failed_import_financing: 'red',
    failed_import_installments: 'red',
    failed_intermediator: 'red',
    generating_endorsement_black: 'yellow',
    in_creation: 'yellow',
    in_progress: 'blue',
    invalidated_by_intermediator: 'red',
    partially_done: 'yellow',
    pending_send_intermediator: 'yellow',
    sending_backing: 'blue',
    sending_cession_intermediator: 'blue',
    sync_installments: 'yellow',
    synced_installments: 'green',
  };

  return colorMap[status] || 'gray';
}
